import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentTenant } from "./useTenants";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "./useProfile";

export interface PlatformAnnouncement {
  id: string;
  title: string;
  content: string;
  type: string;
  is_active: boolean;
  is_dismissible: boolean;
  display_format: string;
  trigger_type: string;
  target_app_roles: string[] | null;
  target_clinic_roles: string[] | null;
  target_user_ids: string[] | null;
  target_tenant_ids: string[] | null;
  priority: number;
  require_acknowledgment: boolean;
  starts_at: string;
  ends_at: string | null;
  created_at: string;
}

export const useTenantAnnouncements = () => {
  const { currentTenantId, isLoading: tenantLoading } = useCurrentTenant();
  const { user } = useAuth();
  const { data: profile } = useProfile();

  return useQuery({
    queryKey: ["tenant-portal-announcements", currentTenantId, user?.id],
    queryFn: async (): Promise<PlatformAnnouncement[]> => {
      const now = new Date().toISOString();

      const { data: announcements, error } = await supabase
        .from("platform_announcements")
        .select("*")
        .eq("is_active", true)
        .lte("starts_at", now)
        .order("priority", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Get user's app roles
      let userAppRoles: string[] = [];
      if (user?.id) {
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);
        userAppRoles = roles?.map((r) => r.role) || [];
      }

      // Get user's clinic roles from profile
      const userClinicRoles = profile?.clinic_roles?.map((cr) => cr.role) || [];

      // Filter announcements
      const activeAnnouncements = (announcements || []).filter((a: any) => {
        // Filter expired
        if (a.ends_at && new Date(a.ends_at) < new Date()) return false;

        // Filter by target tenant
        if (a.target_tenant_ids && a.target_tenant_ids.length > 0) {
          if (!currentTenantId || !a.target_tenant_ids.includes(currentTenantId)) return false;
        }

        // Filter by target app roles
        if (a.target_app_roles && a.target_app_roles.length > 0) {
          if (!userAppRoles.some((r) => a.target_app_roles.includes(r))) return false;
        }

        // Filter by target clinic roles
        if (a.target_clinic_roles && a.target_clinic_roles.length > 0) {
          if (!userClinicRoles.some((r) => a.target_clinic_roles.includes(r))) return false;
        }

        // Filter by target user IDs
        if (a.target_user_ids && a.target_user_ids.length > 0) {
          if (!user?.id || !a.target_user_ids.includes(user.id)) return false;
        }

        return true;
      });

      // Get dismissed announcements
      if (user?.id) {
        const { data: dismissals } = await supabase
          .from("announcement_dismissals")
          .select("announcement_id")
          .eq("user_id", user.id);

        const dismissedIds = new Set(dismissals?.map((d) => d.announcement_id) || []);
        return activeAnnouncements.filter((a: any) => !dismissedIds.has(a.id));
      }

      return activeAnnouncements;
    },
    enabled: !tenantLoading && !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
};

// Grouped announcements by display format
export const useGroupedAnnouncements = () => {
  const { data: announcements } = useTenantAnnouncements();

  const banners = announcements?.filter((a) => a.display_format === "banner") || [];
  const toasts = announcements?.filter((a) => a.display_format === "toast") || [];
  const modals = announcements?.filter((a) => a.display_format === "modal") || [];
  const floatingBanners = announcements?.filter((a) => a.display_format === "floating_banner") || [];
  const loginAnnouncements = announcements?.filter((a) => a.trigger_type === "on_login") || [];

  return { banners, toasts, modals, floatingBanners, loginAnnouncements };
};

export const useDismissAnnouncement = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ announcementId, acknowledged = false }: { announcementId: string; acknowledged?: boolean }) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("announcement_dismissals")
        .insert({
          announcement_id: announcementId,
          user_id: user.id,
          acknowledged,
        });

      if (error && !error.message.includes("duplicate")) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-portal-announcements"] });
    },
  });
};

export const useAnnouncementCount = () => {
  const { data: announcements } = useTenantAnnouncements();
  return announcements?.length || 0;
};
